/**
 * OutcomeSimulationModal - Full Outcome Simulation advisory modal
 *
 * Atomic level: Organism
 * Used in: RecordDetail
 *
 * Displays comprehensive FOS outcome prediction with:
 * - Prediction badge and confidence
 * - FOS Assessment Reasoning with DRN links
 * - Key Decision Factors
 * - Potential Redress Guidance
 * - Similar FOS Decisions
 * - Processing metadata and disclaimer
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import { OutcomePredictionBadge } from './OutcomePredictionBadge';
import { ConfidenceBadge } from './ConfidenceBadge';
import IconWrapper from '../../../ui/IconWrapper';
import { FiChevronDown, FiChevronUp, FiClock, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import type { OutcomeSimulation, SimilarFOSDecision, OutcomePrediction } from '../../../../types/record';

interface OutcomeSimulationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outcomeSimulation: OutcomeSimulation;
}

/**
 * Format currency value for display
 */
const formatCurrency = (value: number | undefined, currency?: string): string => {
  if (typeof value !== 'number') return '';
  const c = (currency || 'GBP').toUpperCase();
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c }).format(value);
  } catch {
    const prefix = c === 'GBP' ? '£' : c === 'EUR' ? '€' : c === 'USD' ? '$' : '';
    return `${prefix}${value.toFixed(0)}`;
  }
};

/**
 * Format timestamp for display
 */
const formatGeneratedTime = (timestamp?: string): string => {
  if (!timestamp) return 'Unknown';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Unknown';
  }
};

/**
 * Get FOS decision URL
 */
const getFOSDecisionUrl = (decisionId: string): string => {
  return `https://www.financial-ombudsman.org.uk/decision/${decisionId}`;
};

/**
 * Get outcome badge styling for similar decisions
 */
const getDecisionOutcomeStyles = (outcome: string): string => {
  const normalized = outcome.toLowerCase();
  switch (normalized) {
    case 'upheld':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'not_upheld':
    case 'not upheld':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'partially_upheld':
    case 'partially upheld':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

/**
 * Render text with DRN links
 */
const renderWithDRNLinks = (text: string): React.ReactNode => {
  const parts = text.split(/(DRN-\d+)/g);
  return parts.map((part, idx) => {
    if (/^DRN-\d+$/.test(part)) {
      return (
        <a
          key={idx}
          href={getFOSDecisionUrl(part)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline font-medium"
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
};

const OutcomeSimulationModal: React.FC<OutcomeSimulationModalProps> = ({
  open,
  onOpenChange,
  outcomeSimulation,
}) => {
  const [similarDecisionsExpanded, setSimilarDecisionsExpanded] = useState(false);

  const {
    prediction,
    confidence,
    reasoning,
    keyFactors,
    redressGuidance,
    redressEvidence,
    similarDecisions,
    generatedAt,
    processingMetadata,
    activatedSkills,
  } = outcomeSimulation;

  const isUpheldOrPartial = prediction === 'likely_upheld';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Outcome Simulation</DialogTitle>
          <DialogDescription>
            AI-powered prediction based on similar FOS decisions
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Header with Prediction and Confidence */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <OutcomePredictionBadge prediction={prediction} size="lg" />
            <ConfidenceBadge confidence={confidence} size="lg" />
          </div>

          {/* Activated Skills */}
          {activatedSkills && activatedSkills.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Skills used:</span>
              {activatedSkills.map((skill: string) => (
                <Badge
                  key={skill}
                  variant="outline"
                  className="text-xs text-muted-foreground bg-muted/50"
                >
                  {skill}
                </Badge>
              ))}
            </div>
          )}

          {/* FOS Assessment Reasoning */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">
              FOS Assessment Reasoning
            </h3>
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">
                {renderWithDRNLinks(reasoning || 'No reasoning provided.')}
              </p>
              {/* Reference DRNs */}
              {similarDecisions && similarDecisions.length > 0 && (
                <div className="mt-3 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground mr-1">References:</span>
                  {similarDecisions.slice(0, 5).map((d, i) => (
                    <React.Fragment key={d.decisionId}>
                      <a
                        href={getFOSDecisionUrl(d.decisionId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {d.decisionId}
                      </a>
                      {i < Math.min(similarDecisions.length, 5) - 1 && ', '}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Key Decision Factors */}
          {keyFactors && keyFactors.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Key Decision Factors
              </h3>
              <div className="space-y-2">
                {keyFactors.map((factor, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2" />
                    <span className="text-sm text-foreground">{factor}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Redress Guidance (only for upheld/partially upheld) */}
          {isUpheldOrPartial && redressGuidance && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Potential Redress Guidance
              </h3>
              <div className="bg-muted/30 rounded-lg p-4 border border-border text-sm">
                {/* Weak evidence warning */}
                {redressEvidence?.level === 'weak' && (
                  <div className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2">
                    <IconWrapper icon={FiAlertTriangle} className="size-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Caution: limited precedent evidence. Treat this guidance as indicative only.
                    </span>
                  </div>
                )}

                {/* Monetary range */}
                {redressGuidance.monetaryRange && (
                  <div className="mb-2">
                    <span className="font-medium">Typical monetary range: </span>
                    {formatCurrency(redressGuidance.monetaryRange.min, redressGuidance.monetaryRange.currency)}
                    {' – '}
                    {formatCurrency(redressGuidance.monetaryRange.max, redressGuidance.monetaryRange.currency)}
                  </div>
                )}

                {/* Common remedies */}
                {redressGuidance.types && redressGuidance.types.length > 0 && (
                  <div className="mb-2">
                    <span className="font-medium">Common remedies: </span>
                    {redressGuidance.types.map((t) => t.replace(/_/g, ' ')).join(', ')}
                  </div>
                )}

                {/* Precedents */}
                {redressGuidance.precedents && redressGuidance.precedents.length > 0 && (
                  <div className="mb-2">
                    <span className="font-medium">Precedents: </span>
                    {redressGuidance.precedents.slice(0, 3).map((ref, idx) => (
                      <React.Fragment key={ref}>
                        <a
                          href={getFOSDecisionUrl(ref)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {ref}
                        </a>
                        {idx < Math.min(redressGuidance.precedents!.length, 3) - 1 && ', '}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Notes */}
                {redressGuidance.notes && (
                  <div className="text-foreground whitespace-pre-line">
                    {redressGuidance.notes}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Similar FOS Decisions */}
          {similarDecisions && similarDecisions.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setSimilarDecisionsExpanded(!similarDecisionsExpanded)}
                className="flex items-center justify-between w-full text-left"
                aria-expanded={similarDecisionsExpanded}
              >
                <h3 className="text-sm font-semibold text-foreground">
                  Precedent Cases ({similarDecisions.length})
                </h3>
                <IconWrapper
                  icon={similarDecisionsExpanded ? FiChevronUp : FiChevronDown}
                  className="size-4 text-muted-foreground"
                />
              </button>

              {similarDecisionsExpanded && (
                <div className="mt-3 space-y-3">
                  {similarDecisions.slice(0, 5).map((decision) => (
                    <SimilarDecisionCard key={decision.decisionId} decision={decision} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Metadata footer */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <IconWrapper icon={FiClock} className="size-3" />
                  <span>Generated {formatGeneratedTime(generatedAt)}</span>
                </div>
                {processingMetadata && (
                  <span>
                    Based on {processingMetadata.decisionsAnalyzed || 0} similar decisions
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-xs text-primary/80">
              <strong>Disclaimer:</strong> This is an opinion generated based on historical FOS
              decisions. It should be used as guidance only and does not guarantee actual FOS
              outcomes. Always consider the specific circumstances of each case.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SimilarDecisionCard - Card for a similar FOS decision
 */
interface SimilarDecisionCardProps {
  decision: SimilarFOSDecision;
}

const SimilarDecisionCard: React.FC<SimilarDecisionCardProps> = ({ decision }) => {
  return (
    <div className="bg-muted/30 rounded-lg p-3 border border-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <a
          href={getFOSDecisionUrl(decision.decisionId)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
        >
          {decision.decisionId}
          <IconWrapper icon={FiExternalLink} className="size-3" />
        </a>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={cn('text-xs', getDecisionOutcomeStyles(decision.outcome))}
          >
            {decision.outcome}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {Math.round(decision.similarityScore * 100)}% similar
          </span>
        </div>
      </div>

      {/* Interpretation */}
      {decision.interpretation && (
        <div className="text-xs text-foreground">
          <div className="font-medium text-muted-foreground mb-1">How this relates:</div>
          <p className="leading-relaxed mb-2">{decision.interpretation}</p>
        </div>
      )}

      {/* Key factors */}
      {decision.keyFactors && decision.keyFactors.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-medium text-muted-foreground mb-1">Key factors:</div>
          <div className="flex flex-wrap gap-1">
            {decision.keyFactors.map((factor, idx) => (
              <span
                key={idx}
                className="inline-block px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
              >
                {factor}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Match reasoning */}
      {decision.matchReasoning && (
        <div className="mt-2 text-[11px] text-muted-foreground line-clamp-2">
          {decision.matchReasoning}
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex items-center gap-3">
        {decision.pdfUrl && (
          <a
            href={decision.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            Open PDF
          </a>
        )}
      </div>
    </div>
  );
};

OutcomeSimulationModal.displayName = 'OutcomeSimulationModal';

export { OutcomeSimulationModal };
